import { takeLatest, call, put, select } from 'redux-saga/effects';
import BillingsService from 'shared/services/BillingsService';
import UserService from 'shared/services/UserService';
import { IUser } from 'shared/interfaces/user.interface';
import { IState } from 'shared/interfaces/state.interface';
import { IPaymentMethod } from 'shared/interfaces/payment-method.interface';
import { TStripeIntent, TAddPaymentMethodAction, TAction } from 'shared/types';
import {
  createStripeSetupIntent,
  setStripeSetupInter,
  addPaymentMethod,
  getUser as getUserAction,
  setCurrentUser,
  getCurrentAffiliate,
  addNewCard,
  getPaymentMethods,
  setPaymentMethods,
  setCurrentAffiliate,
  updateAffiliateDataSuccess,
  updateAffiliateDataFail,
  updateAffiliateData,
  setPaymentProcessorsKeys,
  getAffiliatePaymentProcessorsKeys,
  updateAffiliatePaymentProcessorsKeys,
  updateAffiliatePaymentProcessorsKeysSuccess,
  updateAffiliatePaymentProcessorsKeysFail
} from './settings.redux';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';
import { AxiosResponse } from 'axios';
import {
  IAffiliate,
  IAffiliateSettingsUpdate
} from 'shared/interfaces/affiliate.interface';
import {
  IPaymentProcessorKeys,
  IPaymentProcessorKey
} from 'shared/interfaces/payment-processor-keys.interface';

function* getUser() {
  try {
    const user: IUser = yield call(UserService.getMe);

    yield put(setCurrentUser(user));
  } catch (error) {
    console.log('error', error);
  }
}

function* getAffiliate() {
  try {
    const affiliate: IAffiliate = yield call(UserService.getAffiliate);

    yield put(setCurrentAffiliate(affiliate));
  } catch (error) {
    console.log('error', error);
  }
}

function* createSetupIntent() {
  try {
    const stripeIntent: TStripeIntent = yield call(
      BillingsService.createSetupIntent
    );
    yield put(setStripeSetupInter(stripeIntent));
  } catch (error) {
    console.log('error', error);
  }
}

function* addCard({ payload }: TAction<TAddPaymentMethodAction>) {
  try {
    const currentUser: IUser = yield select(
      (s: IState) => s.settings.currentUser
    );

    const data: IPaymentMethod = yield call(BillingsService.addPaymentMethod, {
      ...payload,
      userId: currentUser.id
    });

    yield put(addNewCard(data));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Credit Card was successfully added.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data.message || 'Credit card addition was unsuccesful.'
    });
  }
}

function* getAffiliatePaymentMethods() {
  try {
    const data: IPaymentMethod[] = yield call(
      BillingsService.getAvailablePaymentMethods
    );

    yield put(setPaymentMethods(Array.isArray(data) ? data[0] : null));
  } catch (error) {
    console.log('error', error);
  }
}

function* handleUpdateAffiliateData({
  payload
}: TAction<IAffiliateSettingsUpdate>) {
  try {
    const result: AxiosResponse<IAffiliate> = yield call(
      UserService.updateAffiliate,
      payload
    );
    yield put(updateAffiliateDataSuccess(result));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Affiliate data was successfully updated.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(updateAffiliateDataFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message || 'Affiliate updating was unsuccesful.'
    });
  }
}

function* getPaymentProcessorsKeys() {
  try {
    const response: IPaymentProcessorKeys = yield call(
      BillingsService.getAffiliatePaymentProcessorsKeys
    );

    yield put(setPaymentProcessorsKeys(response.data));
  } catch (error) {
    console.log('error', error);
  }
}

function* handleUpdateAffiliatePaymentProcessorsKeys({ payload }: any) {
  try {
    const response: IPaymentProcessorKeys = yield call(
      BillingsService.updateAffiliatePaymentProcessorsKeys,
      payload
    );
    yield put(updateAffiliatePaymentProcessorsKeysSuccess(response.data));
    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Affiliate data was successfully updated.'
    });
  } catch (error: any) {
    console.log('error', error);
    yield put(updateAffiliatePaymentProcessorsKeysFail());
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message:
        error?.response?.data?.message || 'Affiliate updating was unsuccesful.'
    });
  }
}

function* Settings() {
  yield takeLatest(createStripeSetupIntent.type, createSetupIntent);
  yield takeLatest(addPaymentMethod.type, addCard);
  yield takeLatest(getUserAction.type, getUser);
  yield takeLatest(getPaymentMethods.type, getAffiliatePaymentMethods);
  yield takeLatest(getCurrentAffiliate.type, getAffiliate);
  yield takeLatest(updateAffiliateData.type, handleUpdateAffiliateData);
  yield takeLatest(
    getAffiliatePaymentProcessorsKeys.type,
    getPaymentProcessorsKeys
  );
  yield takeLatest(
    updateAffiliatePaymentProcessorsKeys.type,
    handleUpdateAffiliatePaymentProcessorsKeys
  );
}

export default Settings;
